import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Pagination,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import copy from "../../public/locales/en/textQuality.json";
import TextQualityEditor from "../components/TextQualityEditor";
import { useAppDispatch, useAppSelector } from "../store";
import { loadQualityText, scanQuality } from "../store/slices/textQualitySlice";

import type { ReactElement } from "react";

export default function TextQualityPage(): ReactElement {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { textId } = useParams<{ textId: string }>();
  const state = useAppSelector((store) => store.textQuality);
  const [includeUsers, setIncludeUsers] = useState(state.includeUserTexts);
  const [page, setPage] = useState(1);
  const visiblePage = Math.min(
    page,
    Math.max(1, Math.ceil(state.items.length / 20)),
  );
  const scan = useRef<ReturnType<ReturnType<typeof scanQuality>> | null>(null);
  useEffect((): void => {
    if (textId) void dispatch(loadQualityText(textId));
  }, [dispatch, textId]);
  useEffect((): (() => void) => (): void => scan.current?.abort(), []);
  if (textId)
    return (
      <Box>
        {state.loading ? (
          <CircularProgress />
        ) : state.selected?.id === textId ? (
          <TextQualityEditor
            key={`${state.selected.id}-${state.selected.updatedAt}`}
            report={state.selected}
          />
        ) : (
          <Stack spacing={2}>
            <Button
              onClick={(): void => {
                void navigate("/text-quality");
              }}
            >
              {copy.back}
            </Button>
            {state.error && <Alert severity="error">{state.error}</Alert>}
          </Stack>
        )}
      </Box>
    );
  return (
    <Stack spacing={3}>
      <Typography variant="h4">{copy.title}</Typography>
      <Typography>{copy.intro}</Typography>
      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeUsers}
                disabled={state.running}
                onChange={(_, checked): void => setIncludeUsers(checked)}
              />
            }
            label={copy.includeUsers}
          />
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              disabled={state.running}
              onClick={(): void => {
                setPage(1);
                scan.current = dispatch(scanQuality(includeUsers));
              }}
            >
              {state.running ? copy.running : copy.run}
            </Button>
            {state.running && (
              <>
                <CircularProgress size={24} />
                <Button onClick={(): void => scan.current?.abort()}>
                  {copy.stop}
                </Button>
              </>
            )}
          </Stack>
          {state.started && (
            <Typography>
              {copy.scanned}: {state.scanned} · {copy.found}:{" "}
              {state.items.length}
            </Typography>
          )}
        </Stack>
      </Paper>
      {state.error && <Alert severity="error">{state.error}</Alert>}
      {state.started && !state.running && !state.completed && (
        <Alert severity="info">{copy.partial}</Alert>
      )}
      {state.completed && !state.items.length && (
        <Alert severity="success">{copy.empty}</Alert>
      )}
      <List disablePadding>
        {state.items
          .slice((visiblePage - 1) * 20, visiblePage * 20)
          .map((item) => (
            <ListItem key={item.id} disablePadding sx={{ mb: 1 }}>
              <Paper sx={{ width: "100%" }}>
                <ListItemButton
                  disabled={state.running}
                  onClick={(): void => {
                    void navigate(`/text-quality/${item.id}`);
                  }}
                  aria-label={`${copy.review}: ${item.topic || copy.untitled}`}
                >
                  <ListItemText
                    primary={`${item.topic || copy.untitled} · ${item.language} · ${item.level}`}
                    secondary={`${item.authorId ? copy.user : copy.system} · ${copy.issues}: ${item.issues.length}`}
                    sx={{ overflowWrap: "anywhere" }}
                  />
                </ListItemButton>
              </Paper>
            </ListItem>
          ))}
      </List>
      {state.items.length > 20 && (
        <Pagination
          size="small"
          count={Math.ceil(state.items.length / 20)}
          page={visiblePage}
          onChange={(_, value): void => setPage(value)}
        />
      )}
    </Stack>
  );
}
